import React from "react";
import Section from "../ui/Section/Section";
import { Grid, Stack } from "../ui/Grid/Grid";
import KpiCard from "../ui/Card/KpiCard";
import ChartCard from "../ui/Card/ChartCard";
import { CenterRow } from "../ui/Card/Card";
import Donut from "../charts/Donut";
import Pyramid from "../charts/Pyramid";
import Legend from "../ui/Legend/Legend";
import SectionSkeleton from "../ui/SectionSkeleton/SectionSkeleton";
import EmptyState from "../ui/EmptyState/EmptyState";
import { KPI, SECTIONS } from "../../constants";
import { fmt1, fmt1Signed } from "../../functions/format";
import { pctChange } from "../../functions/mapEffectif";
import { CHART_COLORS } from "../../styles/theme";
import { usePilotageRhStore } from "../../store/usePilotageRhStore";

const { id, title } = SECTIONS.diversite;

const SKELETON_ROWS = [{ cols: 3, cards: [5, 5, 3] }];

/** Textes des tooltips "vs précédent" ; ajuster les suffixes ici. */
const formatPctDeltaTip = (current, previous) => {
    const delta = pctChange(current, previous);
    return delta !== null ? `${fmt1Signed(delta)}% vs précédent` : undefined;
};

const formatPointDeltaTip = (current, previous) =>
    previous !== null && previous !== undefined
        ? `${fmt1Signed(current - previous)}pt vs précédent`
        : undefined;

const DiversiteSection = ({ data, isLoading }) => {
    const resetFilters = usePilotageRhStore((s) => s.resetFilters);

    if (isLoading && !data) {
        return (
            <Section id={id} title={title}>
                <SectionSkeleton rows={SKELETON_ROWS} />
            </Section>
        );
    }

    if (!data || data.total === 0) {
        return (
            <Section id={id} title={title}>
                <EmptyState
                    title="Aucun collaborateur ne correspond à ce périmètre"
                    description="Modifiez les filtres pour afficher la démographie."
                    actionLabel="Réinitialiser les filtres"
                    onAction={resetFilters}
                />
            </Section>
        );
    }

    const { genre } = data;

    return (
        <Section id={id} title={title}>
            <Grid cols={3}>
                <ChartCard title="Pyramide des âges" fill>
                    <Pyramid rows={data.pyramide} />
                    <Legend
                        items={[
                            { name: "Femmes", color: CHART_COLORS.violet },
                            { name: "Hommes", color: CHART_COLORS.blue },
                        ]}
                    />
                </ChartCard>

                <ChartCard title="Répartition par genre">
                    <CenterRow>
                        <Donut
                            segments={[
                                {
                                    value: genre.femmes,
                                    color: CHART_COLORS.violet,
                                    tip: `Femmes : ${genre.femmes} collab.`,
                                },
                                {
                                    value: genre.hommes,
                                    color: CHART_COLORS.blue,
                                    tip: `Hommes : ${genre.hommes} collab.`,
                                },
                                {
                                    value: genre.autre,
                                    color: CHART_COLORS.violetSoft,
                                    tip: `Autre : ${genre.autre} collab.`,
                                },
                            ]}
                        />
                    </CenterRow>
                    <Legend
                        items={[
                            {
                                name: `Femmes ${genre.femmesPct}%`,
                                color: CHART_COLORS.violet,
                            },
                            {
                                name: `Hommes ${genre.hommesPct}%`,
                                color: CHART_COLORS.blue,
                            },
                            {
                                name: `Autre ${genre.autrePct}%`,
                                color: CHART_COLORS.violetSoft,
                            },
                        ]}
                    />
                </ChartCard>

                <Stack>
                    <KpiCard
                        label={KPI.age_moyen.label}
                        unit={KPI.age_moyen.unit}
                        value={fmt1(data.ageMoyen)}
                        section={id}
                        tip={formatPctDeltaTip(
                            data.ageMoyen,
                            data.ageMoyenPrevious
                        )}
                    />
                    <KpiCard
                        label={KPI.femmes_management.label}
                        unit={KPI.femmes_management.unit}
                        value={fmt1(data.femmesManagement)}
                        section={id}
                        tip={formatPointDeltaTip(
                            data.femmesManagement,
                            data.femmesManagementPrevious
                        )}
                    />
                </Stack>
            </Grid>
        </Section>
    );
};

export default DiversiteSection;
